import * as Utils from '@Utils/Math.js'
import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import { polygon } from '@turf/helpers'

export function outOfBounds(coordinates, size) {
	for (let c of coordinates) {
		if (c[0] >= 0 && c[1] >= 0 && c[0] <= size[0] && c[1] <= size[1]) {
			return false
		}
	}
	return true
}
export function isClockwise(coordinates) {
	let sum = 0
	for (let i = 0; i < coordinates.length; i++) {
		let current = coordinates[i]
		let next = coordinates[(i + 1) % coordinates.length]
		sum += (next[0] - current[0]) * (next[1] + current[1])
	}
	return sum > 0
}

export function getCenter(coordinates) {
	let minX, maxX, minY, maxY
	for (let i = 0; i < coordinates.length; i++) {
		minX = (coordinates[i][0] < minX || minX == null) ? coordinates[i][0] : minX
		maxX = (coordinates[i][0] > maxX || maxX == null) ? coordinates[i][0] : maxX
		minY = (coordinates[i][1] < minY || minY == null) ? coordinates[i][1] : minY
		maxY = (coordinates[i][1] > maxY || maxY == null) ? coordinates[i][1] : maxY
	}
	return [(minX + maxX) / 2, (minY + maxY) / 2]
}
export function translate(coordinates, x, y) {
	let coords = []
	for (let i in coordinates) {
		if (coordinates[i][2] === undefined) {
			coords.push([coordinates[i][0] + x, coordinates[i][1] + y])
		} else {
			coords.push([coordinates[i][0] + x, coordinates[i][1] + y, coordinates[i][2]])
		}
	}
	return coords
}
export function getBoundingBox(coordinates, contraints) {
	let maxY = Number.MIN_SAFE_INTEGER
	let maxX = Number.MIN_SAFE_INTEGER
	let minY = Number.MAX_SAFE_INTEGER
	let minX = Number.MAX_SAFE_INTEGER
	for (let c of coordinates) {
		if (c[0] > maxX) {
			maxX = c[0]
		}
		if (c[0] < minX) {
			minX = c[0]
		}
		if (c[1] > maxY) {
			maxY = c[1]
		}
		if (c[1] < minY) {
			minY = c[1]
		}
	}
	if (contraints) {
		if (contraints.minX !== undefined && minX < contraints.minX) {
			minX = contraints.minX
		}
		if (contraints.minY !== undefined && minY < contraints.minY) {
			minY = contraints.minY
		}
		if (contraints.maxX !== undefined && maxX > contraints.maxX) {
			maxX = contraints.maxX
		}
		if (contraints.maxY !== undefined && maxY > contraints.maxY) {
			maxY = contraints.maxY
		}
	}
	return {
		maxY: maxY,
		maxX: maxX,
		minY: minY,
		minX: minX,
		width: Math.ceil(maxX - minX),
		height: Math.ceil(maxY - minY),
		center: [minX + (maxX - minX) / 2, minY + (maxY - minY) / 2]
	}
}

// to suit your point format, run search/replace for '[0]' and '[1]';
// for 3D version, see 3d branch (configurability would draw significant performance overhead)

// square distance between 2 points
function getSqDist(p1, p2) {
	var dx = p1[0] - p2[0],
		dy = p1[1] - p2[1]

	return dx * dx + dy * dy
}

// square distance from a point to a segment
function getSqSegDist(p, p1, p2) {
	var x = p1[0],
		y = p1[1],
		dx = p2[0] - x,
		dy = p2[1] - y

	if (dx !== 0 || dy !== 0) {
		var t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy)

		if (t > 1) {
			x = p2[0]
			y = p2[1]
		} else if (t > 0) {
			x += dx * t
			y += dy * t
		}
	}

	dx = p[0] - x
	dy = p[1] - y

	return dx * dx + dy * dy
}
// rest of the code doesn't care about point format

// basic distance-based simplification
function simplifyRadialDist(points, sqTolerance) {
	var prevPoint = points[0],
		newPoints = [prevPoint],
		point

	for (var i = 1, len = points.length; i < len; i++) {
		point = points[i]

		if (getSqDist(point, prevPoint) > sqTolerance) {
			newPoints.push(point)
			prevPoint = point
		}
	}

	if (prevPoint !== point) newPoints.push(point)

	return newPoints
}

function simplifyDPStep(points, first, last, sqTolerance, simplified) {
	var maxSqDist = sqTolerance,
		index

	for (var i = first + 1; i < last; i++) {
		var sqDist = getSqSegDist(points[i], points[first], points[last])

		if (sqDist > maxSqDist) {
			index = i
			maxSqDist = sqDist
		}
	}

	if (maxSqDist > sqTolerance) {
		if (index - first > 1) simplifyDPStep(points, first, index, sqTolerance, simplified)
		simplified.push(points[index])
		if (last - index > 1) simplifyDPStep(points, index, last, sqTolerance, simplified)
	}
}

// simplification using Ramer-Douglas-Peucker algorithm
function simplifyDouglasPeucker(points, sqTolerance) {
	var last = points.length - 1

	var simplified = [points[0]]
	simplifyDPStep(points, 0, last, sqTolerance, simplified)
	simplified.push(points[last])

	return simplified
}

// both algorithms combined for awesome performance
export function simplify(points, tolerance, highestQuality) {
	if (points.length <= 2) return points

	var sqTolerance = tolerance !== undefined ? tolerance * tolerance : 1

	points = highestQuality ? points : simplifyRadialDist(points, sqTolerance)
	points = simplifyDouglasPeucker(points, sqTolerance)

	return points
}

export function addMidpointsToRectangle(coordinates) {
	let m1 = getMidpoint(coordinates[0], coordinates[1])
	let m2 = getMidpoint(coordinates[1], coordinates[2])
	let m3 = getMidpoint(coordinates[2], coordinates[3])
	let m4 = getMidpoint(coordinates[3], coordinates[0])
	let newCoordinates = []
	newCoordinates.push(coordinates[0])
	newCoordinates.push(m1)
	newCoordinates.push(coordinates[1])
	newCoordinates.push(m2)
	newCoordinates.push(coordinates[2])
	newCoordinates.push(m3)
	newCoordinates.push(coordinates[3])
	newCoordinates.push(m4)
	newCoordinates.push(coordinates[4])
	return newCoordinates
}
export function getPointDistance(p1, p2) {
	if (p1 && p2) {
		let y = p2[1] - p1[1]
		let x = p2[0] - p1[0]
		return Math.sqrt(x * x + y * y)
	}
	return 0
}
export function getAngle(p1, p2) {
	let cx = p1[0]
	let cy = p1[1]
	let ex = p2[0]
	let ey = p2[1]
	var dy = ey - cy
	var dx = ex - cx
	return Math.atan2(dy, dx)
}
export function getDistance(x1, y1, x2, y2) {
	let y = y2 - y1
	let x = x2 - x1
	return Math.sqrt(x * x + y * y)
}
export function getMidpoint(p1, p2) {
	return [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2]
}
export function movePointAtAngle(point, angle, distance) {
	return [point[0] + Math.sin(angle) * distance, point[1] - Math.cos(angle) * distance]
}
function lengthSquare(X, Y) {
	let xDiff = X[0] - Y[0]
	let yDiff = X[1] - Y[1]
	return xDiff * xDiff + yDiff * yDiff
}
export function getAngles(vertices1, vertices2, vertices3) {
	let a2 = lengthSquare(vertices2, vertices3)
	let b2 = lengthSquare(vertices1, vertices3)
	let c2 = lengthSquare(vertices1, vertices2)

	// Length of sides be a, b, c
	let a = Math.sqrt(a2)
	let b = Math.sqrt(b2)
	let c = Math.sqrt(c2)
	// From Cosine law
	let alpha = Math.acos((b2 + c2 - a2) / (2 * b * c))
	let beta = Math.acos((a2 + c2 - b2) / (2 * a * c))
	let gamma = Math.acos((a2 + b2 - c2) / (2 * a * b))
	return {
		a: a,
		b: b,
		c: c,
		alpha: alpha,
		beta: beta,
		gamma: gamma
	}
}
export function getPerimeter(coordinates) {
	let perimeter = 0
	for (let i = 0; i < coordinates.length - 1; i++) {
		let p1 = coordinates[i]
		let p2 = coordinates[i + 1]
		perimeter += getPointDistance(p1, p2)
	}
	return perimeter
}
export function cleanCoordinateDuplicates(coordinates, i) {
	if (i + 2 > coordinates.length) {
		return coordinates
	}
	let p1 = coordinates[i]
	let p2 = coordinates[i + 1]
	if (p1[0] == p2[0] && p1[1] == p2[1]) {
		coordinates.splice(i + 1, 1)
		return cleanCoordinateDuplicates(coordinates, i)
	}
	return cleanCoordinateDuplicates(coordinates, i + 1)
}
export function cleanCoordinatesByAngle(angle, coordinates, i) {
	if (i + 3 > coordinates.length) {
		return coordinates
	}
	let p1 = coordinates[i]
	let p2 = coordinates[i + 1]
	let p3 = coordinates[i + 2]
	let angles = getAngles(p1, p2, p3)
	if (angles.gamma < angle) {
		coordinates.splice(i + 1, 1)
		return cleanCoordinatesByAngle(angle, coordinates, i)
	}
	return cleanCoordinatesByAngle(angle, coordinates, i + 1)
}
export function cleanCoordinatesByDistance(dist, coordinates, i) {
	if (i + 2 > coordinates.length) {
		return coordinates
	}
	let p1 = coordinates[i]
	let p2 = coordinates[i + 1]
	if (getPointDistance(p1, p2) < dist) {
		coordinates.splice(i + 1, 1)
		return cleanCoordinatesByDistance(dist, coordinates, i)
	}
	return cleanCoordinatesByDistance(dist, coordinates, i + 1)
}
export function cleanCoordinates(coordinates, amount) {
	amount = amount === undefined ? 100 : amount
	amount = amount / 100
	let a = Utils.degreesToRadians(20 * amount)
	let perimeter = getPerimeter(coordinates)
	let d = (perimeter / 35) * amount // 30 for more effect

	coordinates = cleanCoordinateDuplicates(coordinates, 0)
	coordinates = cleanCoordinatesByAngle(a, coordinates, 0)
	coordinates = cleanCoordinatesByDistance(d, coordinates, 0)
	return coordinates
}

export function removeDuplicateAdjacentCoordinates(arr, index) {
	if (index === undefined) {
		index = 0
	}
	let stopped = false
	for (let a = index; a < arr.length; a++) {
		let b
		if (a + 1 < arr.length) {
			b = a + 1
		} else {
			return arr
		}
		if (arr[a][0] === arr[b][0] && arr[a][1] === arr[b][1]) {
			arr.splice(a, 1)
			stopped = true
			break
		}
	}
	if (stopped) {
		return removeDuplicateAdjacentCoordinates(arr, index)
	}
	return arr
}
export function closePath(coordinates) {
	if (coordinates.length > 0 && coordinates.length < 2 && coordinates[0].length > 2) {
		console.warn('closePath: trying to close multiple polygons?')
	}
	if (isClosed(coordinates)) {
		return coordinates
	}
	coordinates.push(coordinates[0])
	return coordinates
}
export function isClosed(coords) {
	if(coords === undefined || coords.length < 1) {
		throw new Error("trying to close a polygon with zero coordinates?")
	}
	return coords[0][0] == coords[coords.length - 1][0] && coords[0][1] == coords[coords.length - 1][1]
}
export function inside(point, poly) {
	return booleanPointInPolygon(point, polygon([poly]))
}